import { useEffect, useRef, useState } from "react";

import Title from "antd/lib/typography/Title";
import { Button, Checkbox, Modal, Spin } from "antd";

import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";
import { AttestationAttributes, UserRole, Year } from "utils/enums";
import { getMimeType } from "utils/helpers";
import { ORGANIZATION_NAME } from "utils/objects";

import astranaLogoPlum  from 'assets/images/Astrana_Logo_Plum.svg';
import { PROVIDER_RESOURCES_URL } from "./urls";
import styles from './Modals.module.scss';
import { Link } from "react-router-dom";

export interface GeneralProviderTrainingRequest {
  UserID: string;
  Version: string | null;
  Timestamp: Date;
}

export interface AttestationAcknowledgementRequest {
  AttestedBy: string;
  AcknowledgementYear: string | null;
  AttestationTypeId: string;
}

export interface TrainingFlags {
  RmiProvTrainingAcknowledgementYear: string[];
}

export interface FaxOptOutProvider{
  exists: boolean;
  companyId: string;
  provKeyId: string;
}

export interface CompanyName{
  companyId: string;
  companyName: string;
}

export interface FaxOptStatus {
  FaxOptOutProviders: FaxOptOutProvider[];
  VendorNames: string[];
  CompanyNames: CompanyName[];
}

export interface AttestationType {
  AttestationTypeId: string;
  AttestationType1: string;
  PopUpContent: string;
  PopUpSubTitle: string;
}

export interface GeneralProviderTrainingProps {
  setShowTraining(flags: boolean): any;
  setAgreementCheckbox(version: string): any; 
  CheckAttestationVisibility(userId: string, attestationName: string): any;
  AddAttestationAcknowledgement(userModel: any): any;
  DownloadAttestation(requestModel: AttestationAcknowledgementRequest): any;
  GetAttestationPopupContent(attestationName: string): AttestationType;
  showTraining: boolean;
  agreementCheckbox: string;
  isCreatingGenProv: boolean;
}

export const GeneralProviderTraining = (props: GeneralProviderTrainingProps) => {   
  
  useEffect(() => {
    CheckAllAttestationsVisibility();
  }, []);

  const CheckAllAttestationsVisibility = async () => {
    await CheckAttestationVisibility(AttestationAttributes.GeneralTrainingAttestation);
  };

  // Get the current year
  const currentYear = new Date().getFullYear().toString();
  const [ type, setType ] = useState('');
  const [ attestationPopupContent, setAttestationPopupContent ] = useState('');
  const [ title, setTitle] = useState('');
  const [ subTitle, setSubTitle] = useState('');

  const CheckAttestationVisibility = async (attestationName: AttestationAttributes) => {
    try {
      const response = await props.CheckAttestationVisibility(sessionStorage.getItem('user_id')!, attestationName);

      if (response) {
        let attestationType: AttestationType = await props.GetAttestationPopupContent(attestationName);
        
        if (attestationType != null ) {
          let content = attestationType.PopUpContent.replaceAll('{PROVIDER_RESOURCES_URL}',PROVIDER_RESOURCES_URL);
          content = content.replaceAll('{ORGANIZATION_NAME}', ORGANIZATION_NAME + ', Inc.');
          setAttestationPopupContent(content);
          setTitle(attestationType.AttestationType1);
          setSubTitle(attestationType.PopUpSubTitle);
          setType(attestationType.AttestationTypeId);

          props.setShowTraining(true);
        }
      }
    } catch (err: any) {
      notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    }
  };

  const submitTraining = () => {   

    const payload = {
      CompanyId: sessionStorage.getItem('ipaGroup'),
      AttestationTypeId: type,
      AttestedDate: new Date(),
      AttestedBy: sessionStorage.getItem('user_id')!,
      AcknowledgementYear: props.agreementCheckbox
    }

    props.AddAttestationAcknowledgement(payload)
      .then(() => {
        DownloadAttestation();
        if (props.agreementCheckbox === currentYear) props.setShowTraining(false);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }
  
  const DownloadAttestation = () => {
    const request = {
      AttestedBy: sessionStorage.getItem('user_id')!,
      AcknowledgementYear: props.agreementCheckbox,
      AttestationTypeId: type
    };

    props.DownloadAttestation(request)
      .then((response: any) => {
        const user_role = sessionStorage.getItem('user_role');
        let fileExtension = "";
  
        // Determine file type and extension based on user_role
        if (user_role === UserRole.ADMIN) {
          fileExtension = ".xlsx";
        } else {
          fileExtension = ".pdf";
        }
  
        let fileName = props.agreementCheckbox + " General Attestation" + fileExtension;
        let blob = new Blob([response]);
        const fileURL = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = fileName;
        anchor.href = fileURL;
        anchor.click();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  return (
    <>
      <Modal
        visible={props.showTraining}
        onCancel={() => props.setShowTraining(false)}
        maskClosable={false}
        closable={false}
        keyboard={false}
        width="1000px"
        footer={[
          <div className="mb-3" style={{ textAlign: "center" }}>
            <input className="mr-2" type="checkbox" onChange={(e) => props.setAgreementCheckbox(e.target.checked ? currentYear : '')} />
            I acknowledge that I have read and understood the information above
          </div>,
          <div style={{textAlign: "center"}}>
            <Button
              type="primary"
              onClick={submitTraining}
              disabled={!props.agreementCheckbox}
              loading={props.isCreatingGenProv}
              >
                Submit
            </Button>
          </div>,
          <div className="row pt-2">
            <div className="col-lg-24 col-md-24">
              <p className="float-left pt-2">&copy; {ORGANIZATION_NAME}, Inc., All Rights Reserved</p>
            </div>
          </div>
        ]}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "20px",
            position: "relative", // Add position relative to the container
          }}
        >
          <img
            src={astranaLogoPlum}
            width={"250px"}
            style={{ position: "absolute", top: 0 }} // Position the image at the top
          />
          <Title level={2} style={{ marginTop: "40px" }}>{title}</Title>
          <Title level={4}>{subTitle}</Title>
        </div>
        <div dangerouslySetInnerHTML={{ __html: attestationPopupContent }}  />
      </Modal>

    </>
  );
};

export interface PNOProviderTrainingProps {
  setShowPNOTraining(flags: boolean): any;
  setPNOAgreementCheckbox(version: string): any;
  CheckAttestationVisibility(userId: string, attestationName: string): any;
  AddAttestationAcknowledgement(userModel: any): any;
  DownloadAttestation(requestModel: AttestationAcknowledgementRequest): any;
  getDownloadResources(filePath: string, companyId: string): any;
  GetAttestationPopupContent(attestationName: string): any;
  showPNOTraining: boolean;
  PNOAgreementCheckbox: string;
  isAddingAttestation: boolean;
}

export const PNOProviderTraining = (props: PNOProviderTrainingProps) => {
  
  const pptDownloaded = useRef(false);
  const pdfDownloaded = useRef(false);
  const [ type, setType ] = useState('');
  const [ attestationPopupContent, setAttestationPopupContent ] = useState('');
  const [ title, setTitle] = useState('');
  const [ subTitle, setSubTitle] = useState('');

  // Get the current year
  const currentYear = new Date().getFullYear().toString();

  useEffect(() => {
    CheckAllAttestationsVisibility();
  }, []);

  const CheckAllAttestationsVisibility = async () => {
    await CheckAttestationVisibility(AttestationAttributes.PNOAttestation);
  };

  const CheckAttestationVisibility = async (attestationName: AttestationAttributes) => {
    try {
      const response = await props.CheckAttestationVisibility(sessionStorage.getItem('user_id')!, attestationName);

      if (response) {
        let attestationType: AttestationType = await props.GetAttestationPopupContent(attestationName);
        
        if (attestationType != null ) {
          let content = attestationType.PopUpContent.replaceAll('{PROVIDER_RESOURCES_URL}',PROVIDER_RESOURCES_URL);
          content = content.replaceAll('{ORGANIZATION_NAME}', ORGANIZATION_NAME + ', Inc.');
          setAttestationPopupContent(content);
          setTitle(attestationType.AttestationType1);
          setSubTitle(attestationType.PopUpSubTitle);
          setType(attestationType.AttestationTypeId);

          props.setShowPNOTraining(true);
        }
      }
    } catch (err: any) {
      notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    }
  };
  
  const submitPNOTraining = () => {
    const payload = {
      CompanyId: sessionStorage.getItem('ipaGroup'),
      AttestationTypeId: type,
      AttestedDate: new Date(),
      AttestedBy: sessionStorage.getItem('user_id')!,
      AcknowledgementYear: props.PNOAgreementCheckbox
    }

    props.AddAttestationAcknowledgement(payload)
      .then(() => {
        DownloadAttestation();
        if (props.PNOAgreementCheckbox === currentYear) props.setShowPNOTraining(false);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }


  const DownloadAttestation = () => {
    const request = {
      AttestedBy: sessionStorage.getItem('user_id')!,
      AcknowledgementYear: props.PNOAgreementCheckbox,
      AttestationTypeId: type
    };

    props.DownloadAttestation(request)
      .then((response: any) => {
        const user_role = sessionStorage.getItem('user_role');
        let fileExtension = "";
  
        // Determine file type and extension based on user_role
        if (user_role === UserRole.ADMIN) {
          fileExtension = ".xlsx";
        } else {
          fileExtension = ".pdf";
        }
  
        let fileName = "PNO Attestation" + fileExtension;
        let blob = new Blob([response]);
        const fileURL = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.download = fileName;
        anchor.href = fileURL;
        anchor.click();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const downloadFile = (filePath: string, companyId: string) => {
    if (filePath == AttestationAttributes.NewProviderOrientationDock) {
      pptDownloaded.current = true;
    } else {
      pdfDownloaded.current = true;
    }
    props.getDownloadResources(filePath, companyId)
      .then((response: string) => {
        const mimeType = getMimeType(filePath);
        let blob = new Blob([response], { type: mimeType });
        const fileURL = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = fileURL;
        anchor.target = "_blank";
        anchor.download = filePath;
        anchor.click();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  return (
    <>
      <Modal
        visible={props.showPNOTraining}
        onCancel={() => props.setShowPNOTraining(false)}
        maskClosable={false}
        closable={false}
        keyboard={false}
        width="1000px"
        footer={[
          <div className="mb-3" style={{ textAlign: "center" }}>
            <Checkbox
              className="mr-2"
              disabled={!(pptDownloaded.current && pdfDownloaded.current) }
              onChange={(e) => props.setPNOAgreementCheckbox(e.target.checked ? currentYear : '')}
            >
              I acknowledge that I have read and understood the information above
            </Checkbox>
          </div>,
          <div style={{textAlign: "center"}}>
            <Button
              type="primary"
              onClick={submitPNOTraining}
              disabled={!props.PNOAgreementCheckbox}
              loading={props.isAddingAttestation}
              >
                Submit
            </Button>
          </div>,
          <div className="row pt-2">
            <div className="col-lg-24 col-md-24">
              <p className="float-left pt-2">&copy; {ORGANIZATION_NAME}, Inc., All Rights Reserved</p>
            </div>
          </div>
        ]}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "20px",
            position: "relative", // Add position relative to the container
          }}
        >
          <img
            src={astranaLogoPlum}
            width={"250px"}
            style={{ position: "absolute", top: 0 }} // Position the image at the top
          />
          <Title level={2} style={{ marginTop: "40px" }}>{title}</Title>
          <Title level={4} style={{ marginTop: "40px" }}>{subTitle}</Title>
        </div>
        <div dangerouslySetInnerHTML={{ __html: attestationPopupContent }} />
          <p style={{ wordSpacing: "3px", width: '100%' }}>
            
            <Link className="ml-1" onClick={() => downloadFile(AttestationAttributes.NewProviderOrientationDock, sessionStorage.getItem('ipaGroup')!)} to={""}>
              New Provider Orientation Deck.pptx
            </Link>
            <br />
            <Link className="ml-1" onClick={() => downloadFile(AttestationAttributes.PNOForm, sessionStorage.getItem('ipaGroup')!)} to={""}>
              PNO Form.pdf
            </Link>
          </p>
      </Modal>

    </>
  );
};
